import React, { useState, useEffect } from 'react';
import clientDB from '../config/clientDatabase';
import './FolderSync.css';

const FolderSync = ({ user, company }) => {
  const [syncConfigs, setSyncConfigs] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newConfig, setNewConfig] = useState({
    clientId: '',
    localPath: '',
    remoteFolder: '',
    syncMode: 'upload', // upload, download, bidirectional
    autoSync: false,
    syncInterval: 60 // minutes
  });
  const [syncStatus, setSyncStatus] = useState({});
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    // Load clients for dropdown
    const clientCompanies = clientDB.getClientCompanies();
    setClients(clientCompanies);

    // Load sync configurations from localStorage
    const storedSyncConfigs = localStorage.getItem('syncConfigurations');
    if (storedSyncConfigs) {
      try {
        const configs = JSON.parse(storedSyncConfigs);
        setSyncConfigs(configs.map(config => ({
          ...config,
          lastSync: config.lastSync ? new Date(config.lastSync) : null
        })));
      } catch (error) {
        setSyncConfigs([]);
      }
    } else {
      setSyncConfigs([]);
    }

    // Load sync logs from localStorage
    const storedLogs = localStorage.getItem('syncLogs');
    if (storedLogs) {
      try {
        const syncLogs = JSON.parse(storedLogs);
        setLogs(syncLogs.map(log => ({
          ...log,
          timestamp: new Date(log.timestamp)
        })));
      } catch (error) {
        setLogs([]);
      }
    } else {
      setLogs([]);
    }
  }, []);

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'synced': return '✅';
      case 'syncing': return '🔄';
      case 'pending': return '⏳';
      case 'error': return '❌';
      default: return '⚪';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'synced': return '#22c55e';
      case 'syncing': return '#3b82f6';
      case 'pending': return '#f59e0b';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getSyncModeIcon = (mode) => {
    switch (mode) {
      case 'upload': return '⬆️';
      case 'download': return '⬇️';
      case 'bidirectional': return '↕️';
      default: return '📁';
    }
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '📋';
    }
  };

  const handleAddConfig = () => {
    if (!newConfig.clientId || !newConfig.localPath || !newConfig.remoteFolder) {
      alert('Please fill in all required fields');
      return;
    }

    const client = clients.find(c => c.id === newConfig.clientId);
    const config = {
      id: Date.now(),
      ...newConfig,
      clientName: client?.name || 'Unknown',
      lastSync: null,
      status: 'pending',
      filesCount: 0,
      totalSize: '0 MB'
    };

    setSyncConfigs(prev => [...prev, config]);
    setNewConfig({
      clientId: '',
      localPath: '',
      remoteFolder: '',
      syncMode: 'upload',
      autoSync: false,
      syncInterval: 60
    });
    setShowAddModal(false);

    // Add log entry
    addLog('info', `New sync configuration created for ${client?.name}`, client?.name);
  };

  const handleManualSync = async (configId) => {
    const config = syncConfigs.find(c => c.id === configId);
    if (!config) return;

    // Update status to syncing
    setSyncConfigs(prev =>
      prev.map(c => c.id === configId ? { ...c, status: 'syncing' } : c)
    );

    addLog('info', `Manual sync started for ${config.clientName}`, config.clientName);

    // Simulate sync process
    setTimeout(() => {
      setSyncConfigs(prev =>
        prev.map(c => c.id === configId ? {
          ...c,
          status: 'synced',
          lastSync: new Date(),
          filesCount: c.filesCount + Math.floor(Math.random() * 5),
        } : c)
      );

      addLog('success', `Manual sync completed for ${config.clientName}`, config.clientName);
    }, 3000);
  };

  const handleToggleAutoSync = (configId, enabled) => {
    setSyncConfigs(prev =>
      prev.map(c => c.id === configId ? { ...c, autoSync: enabled } : c)
    );

    const config = syncConfigs.find(c => c.id === configId);
    const action = enabled ? 'enabled' : 'disabled';
    addLog('info', `Auto-sync ${action} for ${config?.clientName}`, config?.clientName);
  };

  const handleDeleteConfig = (configId) => {
    const config = syncConfigs.find(c => c.id === configId);
    if (!config) return;

    if (window.confirm(`Are you sure you want to delete the sync configuration for ${config.clientName}?`)) {
      setSyncConfigs(prev => prev.filter(c => c.id !== configId));
      addLog('warning', `Sync configuration deleted for ${config.clientName}`, config.clientName);
    }
  };

  const addLog = (type, message, clientName) => {
    const newLog = {
      id: Date.now() + Math.random(),
      type,
      message,
      timestamp: new Date(),
      clientName
    };
    setLogs(prev => [newLog, ...prev.slice(0, 19)]); // Keep last 20 logs
  };

  const handleSelectFolder = () => {
    // In a real app, this would open a native folder picker
    // For now, we'll simulate it
    const samplePaths = [
      '/Users/admin/Reports/Client-Files',
      '/Documents/Reports/Monthly',
      '/Desktop/Client-Reports',
      '/Users/admin/Documents/Exports'
    ];
    const selectedPath = samplePaths[Math.floor(Math.random() * samplePaths.length)];
    setNewConfig(prev => ({ ...prev, localPath: selectedPath }));
  };

  return (
    <div className="folder-sync">
      {/* Header */}
      <div className="sync-header">
        <div className="header-content">
          <h1>Folder Sync</h1>
          <p>Configure local folder synchronization with S3 client folders</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            <span className="btn-icon">➕</span>
            Add Sync Folder
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="sync-stats">
        <div className="stat-item">
          <span className="stat-number">{syncConfigs.length}</span>
          <span className="stat-label">Total Configs</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{syncConfigs.filter(c => c.autoSync).length}</span>
          <span className="stat-label">Auto-Sync</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{syncConfigs.filter(c => c.status === 'synced').length}</span>
          <span className="stat-label">Synced</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {syncConfigs.reduce((sum, c) => sum + c.filesCount, 0)}
          </span>
          <span className="stat-label">Total Files</span>
        </div>
      </div>

      <div className="sync-content">
        {/* Sync Configurations */}
        <div className="sync-configs">
          <div className="configs-header">
            <h2>Sync Configurations</h2>
          </div>

          <div className="configs-list">
            {syncConfigs.map(config => (
              <div key={config.id} className="config-card">
                <div className="config-main">
                  <div className="config-info">
                    <div className="config-client">
                      <h3>{config.clientName}</h3>
                      <span className={`status-badge ${config.status}`}>
                        <span className="status-icon">{getStatusIcon(config.status)}</span>
                        {config.status}
                      </span>
                    </div>

                    <div className="config-paths">
                      <div className="path-item">
                        <span className="path-label">Local:</span>
                        <span className="path-value">{config.localPath}</span>
                      </div>
                      <div className="path-item">
                        <span className="path-label">Remote:</span>
                        <span className="path-value">{config.remoteFolder}</span>
                      </div>
                    </div>

                    <div className="config-details">
                      <div className="detail-item">
                        <span className="detail-icon">{getSyncModeIcon(config.syncMode)}</span>
                        <span className="detail-text">{config.syncMode}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-icon">📁</span>
                        <span className="detail-text">{config.filesCount} files</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-icon">💾</span>
                        <span className="detail-text">{config.totalSize}</span>
                      </div>
                      {config.lastSync && (
                        <div className="detail-item">
                          <span className="detail-icon">⏰</span>
                          <span className="detail-text">Last: {formatTimeAgo(config.lastSync)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="config-actions">
                    <div className="auto-sync-toggle">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={config.autoSync}
                          onChange={(e) => handleToggleAutoSync(config.id, e.target.checked)}
                          className="toggle-input"
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-text">Auto-sync</span>
                      </label>
                      {config.autoSync && (
                        <span className="sync-interval">Every {config.syncInterval}min</span>
                      )}
                    </div>

                    <div className="action-buttons">
                      <button
                        onClick={() => handleManualSync(config.id)}
                        disabled={config.status === 'syncing'}
                        className="btn-sync"
                        title="Start manual sync"
                      >
                        {config.status === 'syncing' ? '🔄' : '▶️'}
                      </button>
                      <button
                        onClick={() => handleDeleteConfig(config.id)}
                        className="btn-delete"
                        title="Delete configuration"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {syncConfigs.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">📁</div>
                <h3>No sync configurations</h3>
                <p>Add your first folder sync to get started</p>
                <button className="btn-primary" onClick={() => setShowAddModal(true)}>
                  Add First Sync Folder
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sync Logs */}
        <div className="sync-logs">
          <div className="logs-header">
            <h2>Sync Activity</h2>
            <button className="view-all-btn">Clear Logs</button>
          </div>

          <div className="logs-list">
            {logs.map(log => (
              <div key={log.id} className={`log-item ${log.type}`}>
                <div className="log-icon">
                  {getLogIcon(log.type)}
                </div>
                <div className="log-content">
                  <p className="log-message">{log.message}</p>
                  <div className="log-meta">
                    <span className="log-time">{formatTimeAgo(log.timestamp)}</span>
                    {log.clientName && (
                      <span className="log-client">{log.clientName}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {logs.length === 0 && (
              <div className="empty-logs">
                <div className="empty-icon">📋</div>
                <p>No sync activity yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Config Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Sync Configuration</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="client-select">Client</label>
                <select
                  id="client-select"
                  value={newConfig.clientId}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, clientId: e.target.value }))}
                  className="form-select"
                >
                  <option value="">Select a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="local-path">Local Folder Path</label>
                <div className="path-input-group">
                  <input
                    id="local-path"
                    type="text"
                    value={newConfig.localPath}
                    onChange={(e) => setNewConfig(prev => ({ ...prev, localPath: e.target.value }))}
                    placeholder="/Users/admin/Reports/ClientName"
                    className="form-input"
                  />
                  <button
                    type="button"
                    onClick={handleSelectFolder}
                    className="btn-select-folder"
                  >
                    Browse
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="remote-folder">Remote S3 Folder</label>
                <input
                  id="remote-folder"
                  type="text"
                  value={newConfig.remoteFolder}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, remoteFolder: e.target.value }))}
                  placeholder="2024-01-Reports"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="sync-mode">Sync Mode</label>
                <select
                  id="sync-mode"
                  value={newConfig.syncMode}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, syncMode: e.target.value }))}
                  className="form-select"
                >
                  <option value="upload">Upload Only (Local → S3)</option>
                  <option value="download">Download Only (S3 → Local)</option>
                  <option value="bidirectional">Bidirectional Sync</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newConfig.autoSync}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, autoSync: e.target.checked }))}
                    />
                    <span>Enable auto-sync</span>
                  </label>
                </div>

                {newConfig.autoSync && (
                  <div className="form-group">
                    <label htmlFor="sync-interval">Interval (minutes)</label>
                    <input
                      id="sync-interval"
                      type="number"
                      min="5"
                      max="1440"
                      value={newConfig.syncInterval}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, syncInterval: parseInt(e.target.value) }))}
                      className="form-input"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleAddConfig}
              >
                Add Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderSync;